import { useCallback } from "react";
import { NavigateOptions, useNavigate } from "react-router-dom";
import { useMutation } from "urql";

import { getForgeSessionUrl } from "@/components/inference/utils";
import { DEFAULT_FORGE_SESSION_NAME } from "@/features/forge/constants";
import {
  SessionAssetFragment,
  SessionFileFragment,
} from "@/features/forge/forge-session/queries.graphql";

import { CreateAsset } from "../../features/asset/queries.graphql";
import { useCreateSession } from "../useCreateSession";
import { useRequiredWorkspace } from "../useRequiredWorkspace";
import useShowSnackbar from "../useShowSnackbar";
import { useUrqlResponseHandler } from "../useUrqlResponseHandler";

interface EditFileInCanvasProps {
  object: EditInCanvasObject;
  sessionId?: string;
  navigateOptions?: NavigateOptions;
}

export type EditInCanvasObject =
  | ({ __typename: "File" } & Pick<SessionFileFragment, "id">)
  | ({ __typename: "Asset" } & Pick<
      SessionAssetFragment,
      "path" | "id" | "sessionId"
    >);

export const useEditInCanvas = () => {
  const { showError } = useShowSnackbar();
  const { getResponseFromState } = useUrqlResponseHandler();
  const { workspace } = useRequiredWorkspace();
  const navigate = useNavigate();
  const [, createSession] = useCreateSession();

  const [, createAsset] = useMutation(CreateAsset);

  const editInCanvas = useCallback(
    async ({
      object,
      sessionId,
      navigateOptions,
    }: EditFileInCanvasProps): Promise<void> => {
      let effectiveSessionId =
        object.__typename === "Asset" ? object.sessionId : sessionId;

      if (!effectiveSessionId) {
        const newSession = await createSession({
          workspace,
          sessionName: DEFAULT_FORGE_SESSION_NAME,
          inferenceFormState: {
            activeMode: "CANVAS_2_D",
          },
          navigateToNewSession: false,
        });
        if (!newSession) {
          showError("Failed to create session");
          return;
        }
        effectiveSessionId = newSession.id;
      }

      if (object.__typename === "Asset") {
        navigate(
          getForgeSessionUrl({
            workspaceName: workspace.name,
            sessionId: effectiveSessionId,
            assetId: object.id,
          }),
          navigateOptions,
        );
      } else if (object.__typename === "File") {
        const result = await createAsset({
          createAssetInput: {
            workspaceId: workspace.id,
            duplicateFromFileId: object.id,
            sessionId: effectiveSessionId,
          },
        });

        const {
          data: { createAsset: createdAsset },
        } = getResponseFromState({ state: result });

        if (createdAsset) {
          navigate(
            getForgeSessionUrl({
              workspaceName: workspace.name,
              sessionId: effectiveSessionId,
              assetId: createdAsset.id,
            }),
            navigateOptions,
          );
        } else {
          showError(
            "Failed to create a Canvas session from the selected file. Please try again.",
          );
        }
      }
    },
    [
      createAsset,
      createSession,
      getResponseFromState,
      navigate,
      showError,
      workspace,
    ],
  );

  return { editInCanvas };
};
